/**
 * Miroir déformant (mode solo) — PUR.
 * Points du visage + intensité (0..1) + temps → points déformés. Le rendu WebGL
 * plaque la vidéo d'origine (coordonnées de texture = points non déformés) sur
 * le maillage déformé.
 *
 * Garanties (testées) : intensité 0 = identité ; contour du visage immobile (pas
 * de déchirure avec le fond) ; aucun triangle retourné ; effets proportionnels
 * à la taille du visage.
 */
import { smoothstep } from '../lib/math';
import type { Point3 } from '../detection/types';
import type { Weights } from './grimaces';

/** Contour du visage (FACE_LANDMARKS_FACE_OVAL de MediaPipe). */
export const FACE_OVAL: readonly number[] = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148,
  176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109,
];

const MESH_SIZE = 468;
const LEFT_EYE = [33, 133];
const RIGHT_EYE = [362, 263];
const LEFT_IRIS = 468;
const RIGHT_IRIS = 473;
const NOSE_TIP = 1;
const MOUTH = [13, 14];
const CHIN = 152;

/** Durée (ms) pour atteindre la déformation maximale. */
export const DEFAULT_RAMP_MS = 8000;
const WOBBLE_PERIOD_MS = 900;
/** Rayon d'action du nez : large, pour un champ doux (les ailes du nez ne se retournent pas). */
const NOSE_R = 1.1;

export function mirrorIntensity(elapsedMs: number, rampMs = DEFAULT_RAMP_MS): number {
  return smoothstep(0, rampMs, elapsedMs);
}

interface Vec {
  x: number;
  y: number;
}

const mean = (pts: readonly Point3[], ids: readonly number[]): Vec => {
  let x = 0;
  let y = 0;
  for (const i of ids) {
    x += (pts[i] as Point3).x;
    y += (pts[i] as Point3).y;
  }
  return { x: x / ids.length, y: y / ids.length };
};

/** Atténuation radiale (1 au centre, 0 au-delà de `r`), douce aux deux bouts. */
const falloff = (d: number, r: number): number => (d >= r ? 0 : (1 - d / r) ** 2);

/** Mélange par défaut (sans grimace) : yeux, nez, bouche étirée, menton qui tremble. */
export const MIXED: Weights = {
  eyes: 0.6,
  nose: 1,
  mouthWide: 0.9,
  mouthOpen: 0,
  squash: 0,
  cross: 0,
  wobble: 0.12,
};

export function deformFace(
  points: readonly Point3[],
  intensity: number,
  t: number,
  weights: Weights = MIXED,
): Point3[] {
  if (points.length < MESH_SIZE || intensity <= 0) return [...points];

  const leftEye = points.length > LEFT_IRIS ? mean(points, [LEFT_IRIS]) : mean(points, LEFT_EYE);
  const rightEye =
    points.length > RIGHT_IRIS ? mean(points, [RIGHT_IRIS]) : mean(points, RIGHT_EYE);
  // Échelle du visage : distance entre les yeux (invariante à la distance caméra).
  const s = Math.hypot(rightEye.x - leftEye.x, rightEye.y - leftEye.y);
  if (!(s > 0)) return [...points];

  const nose = mean(points, [NOSE_TIP]);
  const mouth = mean(points, MOUTH);
  const chin = mean(points, [CHIN]);
  // Axes du visage (suivent la tête penchée) : horizontal, et vertical vers le menton.
  const ax = { x: (rightEye.x - leftEye.x) / s, y: (rightEye.y - leftEye.y) / s };
  const ay = { x: -ax.y, y: ax.x };
  const oval = FACE_OVAL.map((i) => points[i] as Point3);

  const k = Math.min(1, intensity);
  const eyeK = k * weights.eyes;
  const noseK = k * weights.nose;
  const mouthK = k * weights.mouthWide;
  const openK = k * weights.mouthOpen;
  const squashK = k * weights.squash;
  const crossK = k * weights.cross;
  const wobble = k * weights.wobble * s * Math.sin((2 * Math.PI * t) / WOBBLE_PERIOD_MS);
  /** Yeux qui louchent : chaque œil glisse vers le nez, le long de l'axe horizontal. */
  const towardNose = (eye: Vec): number =>
    Math.sign((nose.x - eye.x) * ax.x + (nose.y - eye.y) * ax.y);
  const cross = (p: Vec, eye: Vec): Vec => {
    const f =
      crossK * 0.3 * s * towardNose(eye) * falloff(Math.hypot(p.x - eye.x, p.y - eye.y), 0.5 * s);
    return { x: f * ax.x, y: f * ax.y };
  };

  const magnify = (p: Vec, c: Vec, k: number, r: number): Vec => {
    const dx = p.x - c.x;
    const dy = p.y - c.y;
    const f = k * falloff(Math.hypot(dx, dy), r);
    return { x: dx * f, y: dy * f };
  };

  return points.map((p, i) => {
    if (i >= MESH_SIZE) {
      // Iris : suivent l'œil grossi.
      const c = i < RIGHT_IRIS ? leftEye : rightEye;
      const d = magnify(p, c, eyeK, 0.45 * s);
      const x = cross(p, c);
      return { x: p.x + d.x + x.x, y: p.y + d.y + x.y, z: p.z };
    }
    // Poids nul sur le contour, plein à l'intérieur du visage.
    let toOval = Infinity;
    for (const o of oval) toOval = Math.min(toOval, Math.hypot(p.x - o.x, p.y - o.y));
    const w = smoothstep(0, 0.3 * s, toOval);
    if (w === 0) return p;

    const eyes = [
      magnify(p, leftEye, eyeK, 0.45 * s),
      magnify(p, rightEye, eyeK, 0.45 * s),
      cross(p, leftEye),
      cross(p, rightEye),
      magnify(p, mouth, openK, 0.8 * s),
    ];
    const noseD = magnify(p, nose, noseK, NOSE_R * s);
    // Visage écrasé vers le nez (ou étiré), le long de l'axe vertical.
    const vertical = (p.x - nose.x) * ay.x + (p.y - nose.y) * ay.y;
    const squashF = -squashK * vertical * falloff(Math.hypot(p.x - nose.x, p.y - nose.y), 1.8 * s);
    // Bouche étirée le long de l'axe horizontal du visage.
    const along = (p.x - mouth.x) * ax.x + (p.y - mouth.y) * ax.y;
    const mouthF = mouthK * falloff(Math.hypot(p.x - mouth.x, p.y - mouth.y), 1.0 * s) * along;
    // Menton qui tremble latéralement.
    const wob = wobble * falloff(Math.hypot(p.x - chin.x, p.y - chin.y), 1.0 * s);

    const dx =
      eyes.reduce((sum, e) => sum + e.x, 0) + noseD.x + (mouthF + wob) * ax.x + squashF * ay.x;
    const dy =
      eyes.reduce((sum, e) => sum + e.y, 0) + noseD.y + (mouthF + wob) * ax.y + squashF * ay.y;
    return { x: p.x + w * dx, y: p.y + w * dy, z: p.z };
  });
}
